import { BottomSheetProvider, NetworkStatus } from "@aurem/components";
import { ToastProvider } from "@aurem/ui";
import { useBreadcrumbs } from "components/Topbar/PageTitle";
import { CitizenshipConfirmationProvider } from "providers/employee/CitizenshipConfirmationProvider";
import {
  EmploymentProvider,
  useEmployment,
} from "providers/employee/EmploymentProvider";
import { KycVerificationProvider } from "providers/employee/KycVerificationProvider";
import { ReauthenticationProvider } from "providers/employee/ReauthenticationProvider";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { LayoutSettings } from "../layouts/employee/LayoutSettings";
import UnifiedVerificationLayout from "../layouts/employee/LayoutUnifiedVerification";
import { PageLayout } from "../layouts/PageLayout";
import { NotFound } from "../pages/NotFound/page/not-found.page";
import { BannerProvider } from "../providers/BannerProvider";
import { EmployeeEntry } from "./EmployeeEntry";
import { lazyPage } from "./lazyPage";

const ContributionsSetupV2Page = lazyPage(
  () =>
    import(
      "pages/employee/ContributionsSetup/page/contributions-setup-v2.page"
    ),
);
const WithdrawalsPageV2 = lazyPage(
  () => import("pages/employee/Withdrawals/page/withdrawals.page-v2"),
);
const DashboardPage = lazyPage(() => import("../pages/employee/Dashboard"));
const Overview = lazyPage(
  () => import("../pages/employee/Overview/page/overview.page"),
);
const StatementsPage = lazyPage(
  () => import("../pages/employee/Statements/page/statements.page"),
);
const TransactionsPageV2 = lazyPage(
  () => import("../pages/employee/Transactions/page/transactions.page-v2"),
);
const DisclaimersPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({ default: m.DisclaimersPage })),
);
const FlagDetailsPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({ default: m.FlagDetailsPage })),
);
const KycRequestPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({ default: m.KycRequestPage })),
);
const OnboardingCompletedPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.OnboardingCompletedPage,
  })),
);
const PersonalDetailsPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.PersonalDetailsPage,
  })),
);
const SettingsAccountDetailsPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsAccountDetailsPage,
  })),
);
const SettingsBankDetailsPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsBankDetailsPage,
  })),
);
const SettingsBeneficiaryPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsBeneficiaryPage,
  })),
);
const SettingsEmploymentDetailsPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsEmploymentDetailsPage,
  })),
);
const SettingsSecurityPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsSecurityPage,
  })),
);
const SettingsTaxInformationPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({
    default: m.SettingsTaxInformationPage,
  })),
);
const SubFundsPageV2 = lazyPage(() =>
  import("../pages/employee").then((m) => ({ default: m.SubFundsPageV2 })),
);
const VerificationPage = lazyPage(() =>
  import("../pages/employee").then((m) => ({ default: m.VerificationPage })),
);

const OnboardingRoutes = () => {
  return (
    <Routes>
      <Route element={<FlagDetailsPage />} path="flag-details" />
      <Route element={<PersonalDetailsPage />} path="personal-details" />
      <Route element={<OnboardingCompletedPage />} path="completed" />
    </Routes>
  );
};

const SettingsRoutes = () => {
  const { t } = useI18n();
  const isBeneficiariesEnabled = useFeature("beneficiaries_feature");
  useBreadcrumbs([{ label: t("apps.employee.navigation.settings.title") }]);

  return (
    <Routes>
      <Route element={<Navigate to="account-details" />} index />
      <Route
        element={<SettingsAccountDetailsPage />}
        path="account-details"
        index
      />
      <Route
        element={<SettingsEmploymentDetailsPage />}
        path="employment-details"
      />
      <Route element={<SettingsBankDetailsPage />} path="bank-details" />
      {isBeneficiariesEnabled && (
        <Route element={<SettingsBeneficiaryPage />} path="beneficiaries" />
      )}
      <Route element={<DisclaimersPage />} path="documents" />

      <Route element={<SettingsTaxInformationPage />} path="tax-information" />
      <Route element={<SettingsSecurityPage />} path="security" />
    </Routes>
  );
};

const RequireActiveEmployment = () => {
  const { isTerminated } = useEmployment();

  if (isTerminated) {
    return <NotFound />;
  }

  return <Outlet />;
};

const AuthAppRoutes = () => {
  return (
    <Routes>
      <Route element={<EmployeeEntry />}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route element={<Overview />} path="overview" />

        <Route path="employment/:employmentId/:productAssignmentId">
          <Route element={<UnifiedVerificationLayout />}>
            <Route element={<DashboardPage />} path="dashboard" />
            <Route element={<WithdrawalsPageV2 />} path="withdrawals" />
            <Route element={<RequireActiveEmployment />}>
              <Route
                element={<ContributionsSetupV2Page />}
                path="contributions-setup"
              />
            </Route>
            <Route element={<SubFundsPageV2 />} path="sub-funds" />
            <Route element={<TransactionsPageV2 />} path="transactions" />
            <Route element={<StatementsPage />} path="statements" />
            <Route element={<VerificationPage />} path="verification" />
          </Route>
        </Route>

        <Route
          element={<KycRequestPage />}
          path="employment/:employmentId/:productAssignmentId/kyc-request/:id"
        />
        <Route element={<KycRequestPage />} path="kyc-request/:id" />

        <Route element={<LayoutSettings />}>
          <Route element={<SettingsRoutes />} path="settings/*" />
        </Route>

        <Route element={<OnboardingRoutes />} path="onboarding/*" />
        <Route
          element={
            <PageLayout>
              <DisclaimersPage showActionButton />
            </PageLayout>
          }
          path="disclaimers"
        />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export const EmployeeRoutes = () => {
  return (
    <BannerProvider>
      <BottomSheetProvider>
        <EmploymentProvider>
          <CitizenshipConfirmationProvider>
            <KycVerificationProvider>
              <ReauthenticationProvider>
                <NetworkStatus />
                <AuthAppRoutes />
                <ToastProvider />
              </ReauthenticationProvider>
            </KycVerificationProvider>
          </CitizenshipConfirmationProvider>
        </EmploymentProvider>
      </BottomSheetProvider>
    </BannerProvider>
  );
};
